import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Upload, BarChart3, Users2, ArrowRight, Sparkles, X } from "lucide-react";

/**
 * 3-slide welcome modal shown to a brand-new tenant immediately after
 * they finish the self-serve `/start-trial` wizard. Rendered inside the
 * Dashboard page when `?welcome=1` is present in the URL. Each slide
 * points to the next thing the user should try; the final slide drops
 * them onto the upload screen so they can ingest data immediately.
 *
 * The modal can be dismissed any time, and a `kinntegra_tour_seen`
 * flag in localStorage prevents it from reappearing for the same user
 * on subsequent logins.
 */
const SLIDES = [
  {
    icon: Upload,
    title: "Ingest your first report",
    body: "Drop a Karvy or CAMS file on the Cron Jobs page — we parse it instantly and pre-populate your investor & AUM masters. Your data never leaves our tenant-scoped vault.",
    cta: { label: "Take me to upload", path: "/cronjobs" },
  },
  {
    icon: BarChart3,
    title: "See the consolidated dashboard",
    body: "All metrics — Exited Clients, Reduced Portfolios, Underperformers, KYC Mismatches — are grouped PAN-wise across folios with full multi-tier fallback. Drill down to any client.",
    cta: { label: "Show me the dashboard", path: "/dashboard" },
  },
  {
    icon: Users2,
    title: "Invite your team",
    body: "Add sub-brokers and RM seats from the Sub-Brokers module. Each one gets a scoped view of just their clients — you stay in control of the master data.",
    cta: { label: "Open Sub-Brokers", path: "/sub-brokers/list" },
  },
];

export default function WelcomeTour() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const [slide, setSlide] = useState(0);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (params.get("welcome") === "1" && !localStorage.getItem("kinntegra_tour_seen")) {
      setOpen(true);
    }
  }, [params]);

  if (!open) return null;

  const close = (markSeen = true) => {
    if (markSeen) localStorage.setItem("kinntegra_tour_seen", "1");
    setOpen(false);
    // strip the `?welcome=1` param without a full reload
    const p = new URLSearchParams(params);
    p.delete("welcome");
    setParams(p, { replace: true });
  };

  const current = SLIDES[slide];
  const Icon = current.icon;
  const isLast = slide === SLIDES.length - 1;

  const handleCta = () => {
    close();
    navigate(current.cta.path);
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" data-testid="welcome-tour-modal">
      <div className="w-full max-w-lg rounded-2xl bg-gradient-to-b from-[#0f1f1d] to-[#07111F] border border-[#C8A96B]/30 shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/5">
          <div className="flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-[#C8A96B]">
            <Sparkles size={14} />
            Welcome to Kinntegra
          </div>
          <button
            onClick={() => close(true)}
            className="text-white/40 hover:text-white/80 transition"
            data-testid="welcome-tour-close"
            aria-label="Close"
          >
            <X size={16} />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 sm:px-9 py-8 text-white">
          <div className="h-12 w-12 rounded-xl bg-[#C8A96B]/15 border border-[#C8A96B]/30 flex items-center justify-center text-[#C8A96B] mb-5">
            <Icon size={22} />
          </div>
          <h3 className="text-xl sm:text-2xl font-light mb-3" data-testid="welcome-tour-title">{current.title}</h3>
          <p className="text-sm text-white/65 leading-relaxed">{current.body}</p>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-white/5 bg-white/[0.02]">
          {/* Dots */}
          <div className="flex gap-1.5">
            {SLIDES.map((_, i) => (
              <div
                key={i}
                className={`h-1.5 rounded-full transition-all ${i === slide ? "w-6 bg-[#C8A96B]" : "w-1.5 bg-white/15"}`}
              />
            ))}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => close(true)}
              className="text-xs uppercase tracking-[0.16em] text-white/40 hover:text-white/70 px-3 py-2 transition"
              data-testid="welcome-tour-skip"
            >
              Skip tour
            </button>
            {!isLast ? (
              <button
                onClick={() => setSlide((s) => s + 1)}
                className="bg-[#C8A96B] text-[#07111F] hover:bg-[#d4b87a] rounded-lg px-4 py-2 text-xs uppercase tracking-[0.16em] font-medium flex items-center gap-1.5 transition"
                data-testid="welcome-tour-next"
              >
                Next <ArrowRight size={13} />
              </button>
            ) : (
              <button
                onClick={handleCta}
                className="bg-[#C8A96B] text-[#07111F] hover:bg-[#d4b87a] rounded-lg px-4 py-2 text-xs uppercase tracking-[0.16em] font-medium flex items-center gap-1.5 transition"
                data-testid="welcome-tour-cta"
              >
                {current.cta.label} <ArrowRight size={13} />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
